// Renders the main input fields for the contact form (name, email, company, message)
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { contactFormCopy } from '@/contexts/language/translations/pages/contactForm';
import { 
  FormField, 
  FormItem, 
  FormLabel, 
  FormControl, 
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Props for the FormFields component
interface FormFieldsProps {
  form: UseFormReturn<any>; // The react-hook-form instance
}

const FormFields: React.FC<FormFieldsProps> = ({ form }) => {
  const { language } = useLanguage();
  const c = contactFormCopy[language];
  
  return (
    <>
      {/* Name field */}
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">{c.fullName}</FormLabel>
            <FormControl>
              <Input placeholder={c.fullNamePlaceholder} className="bg-white text-muted-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {/* Email field */}
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">{c.workEmail}</FormLabel>
            <FormControl>
              <Input placeholder={c.workEmailPlaceholder} type="email" className="bg-white text-muted-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {/* Company field */}
      <FormField
        control={form.control}
        name="company"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">{c.company}</FormLabel>
            <FormControl>
              <Input placeholder={c.companyPlaceholder} className="bg-white text-muted-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {/* Company size field */}
      <FormField
        control={form.control}
        name="companySize"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">{c.companySize} <span className="text-muted-foreground text-xs font-normal">{c.optional}</span></FormLabel>
            <Select onValueChange={field.onChange} value={field.value || undefined}>
              <FormControl>
                <SelectTrigger className="bg-white text-muted-foreground">
                  <SelectValue placeholder={c.companySizePlaceholder} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="1-15">{c.companySizeOptions[0]}</SelectItem>
                <SelectItem value="16-50">{c.companySizeOptions[1]}</SelectItem>
                <SelectItem value="50-200">{c.companySizeOptions[2]}</SelectItem>
                <SelectItem value="more than 200">{c.companySizeOptions[3]}</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {/* Message field */}
      <FormField
        control={form.control}
        name="message"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">{c.howCanWeHelp} <span className="text-muted-foreground text-xs font-normal">{c.optional}</span></FormLabel>
            <FormControl>
              <Textarea 
                placeholder="" 
                className="resize-none bg-white text-muted-foreground"
                rows={4} 
                {...field} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Human verification: 7 + 1 (arrows to change value) */}
      <FormField
        control={form.control}
        name="humanCheck"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">{c.verifyHuman}</FormLabel>
            <FormControl>
              <Input
                type="number"
                min={0}
                max={20}
                step={1}
                placeholder="?"
                className="bg-white text-foreground w-32 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                ref={field.ref}
                value={field.value === undefined || field.value === null ? '' : field.value}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') {
                    field.onChange(undefined);
                    return;
                  }
                  const n = parseInt(v, 10);
                  field.onChange(Number.isNaN(n) ? undefined : n);
                }}
                onBlur={field.onBlur}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

export default FormFields;
